import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Download, FilterX, ShoppingBag, IndianRupee, Package, ChevronDown, X, Ban, RotateCcw, Pencil, Plus, Minus } from "lucide-react";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { downloadExcel } from "../../lib/excel";
import { toast } from "sonner";
import { WhatsAppShareButton } from "../../components/WhatsAppShareButton";
import { notifyStockWarnings } from "../../lib/stock";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function AdminOrders() {
  const today = todayIso();
  const firstOfMonth = today.slice(0, 8) + "01";

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [storeMap, setStoreMap] = useState(new Map());
  // Draft filter state (what the user is editing)
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [productIds, setProductIds] = useState([]); // multi-select
  const [productSearch, setProductSearch] = useState("");
  const [productPopOpen, setProductPopOpen] = useState(false);
  const [salesmanId, setSalesmanId] = useState("all");
  const [storeType, setStoreType] = useState("all");
  // Applied filter state (what's used to filter the data)
  const [applied, setApplied] = useState({
    from: firstOfMonth,
    to: today,
    productIds: [],
    salesmanId: "all",
    storeType: "all",
  });
  const [selected, setSelected] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRefund, setCancelRefund] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);
  const [editItemsOpen, setEditItemsOpen] = useState(false);
  const [editItems, setEditItems] = useState({}); // {pid: qty}
  const [editProductSearch, setEditProductSearch] = useState("");
  const [editStoreType, setEditStoreType] = useState("retail");
  const [editSaving, setEditSaving] = useState(false);

  const refresh = async () => {
    const r = await api.get("/orders");
    setOrders(r.data);
    if (selected) {
      const fresh = r.data.find((x) => x.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };

  const openCancel = () => {
    setCancelReason("");
    setCancelRefund("");
    setCancelOpen(true);
  };

  const submitCancel = async () => {
    if (!selected) return;
    setCancelSaving(true);
    try {
      const payload = {};
      if (cancelReason.trim()) payload.reason = cancelReason.trim();
      const refund = Number(cancelRefund);
      if (refund > 0) payload.refund_amount = refund;
      await api.patch(`/orders/${selected.id}/cancel`, payload);
      toast.success("Order cancelled");
      setCancelOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCancelSaving(false);
    }
  };

  const restoreOrder = async () => {
    if (!selected) return;
    if (!window.confirm("Restore this cancelled order?")) return;
    try {
      await api.patch(`/orders/${selected.id}/restore`);
      toast.success("Order restored");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const openEditItems = () => {
    if (!selected) return;
    const m = {};
    for (const it of selected.items) m[it.product_id] = it.quantity;
    setEditItems(m);
    setEditProductSearch("");
    setEditStoreType(selected.store_type === "wholesale" ? "wholesale" : "retail");
    setEditItemsOpen(true);
  };

  const submitEditItems = async () => {
    if (!selected) return;
    const items = Object.entries(editItems)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    if (items.length === 0) return toast.error("At least one item must remain. Use Cancel order to fully cancel.");
    setEditSaving(true);
    try {
      const { data: upd } = await api.put(`/orders/${selected.id}`, {
        items,
        notes: selected.notes,
        store_type: editStoreType,
      });
      toast.success("Order updated");
      notifyStockWarnings(upd.stock_warnings);
      setEditItemsOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const isDirty =
    applied.from !== from ||
    applied.to !== to ||
    applied.salesmanId !== salesmanId ||
    applied.storeType !== storeType ||
    applied.productIds.length !== productIds.length ||
    !applied.productIds.every((x) => productIds.includes(x));

  const submitFilters = () => {
    setApplied({ from, to, productIds: [...productIds], salesmanId, storeType });
  };

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/users/salesmen").then((r) => setSalesmen(r.data));
    api.get("/stores").then((r) => {
      const m = new Map();
      for (const s of r.data) m.set(s.id, s);
      setStoreMap(m);
    });
  }, []);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      const d = o.created_at.slice(0, 10);
      if (d < applied.from || d > applied.to) return false;
      if (applied.productIds.length > 0) {
        if (!o.items.some((it) => applied.productIds.includes(it.product_id))) return false;
      }
      if (applied.salesmanId !== "all" && o.salesman_id !== applied.salesmanId) return false;
      if (applied.storeType !== "all" && o.store_type !== applied.storeType) return false;
      return true;
    });
  }, [orders, applied]);

  // Product breakdown for the filtered date range
  const productBreakdown = useMemo(() => {
    const m = new Map();
    for (const o of filtered) {
      for (const it of o.items) {
        if (applied.productIds.length > 0 && !applied.productIds.includes(it.product_id)) continue;
        const cur = m.get(it.product_id) || {
          product_id: it.product_id,
          product_name: it.product_name,
          qty: 0,
          revenue: 0,
          orders: new Set(),
        };
        cur.qty += it.quantity;
        cur.revenue += it.line_total;
        cur.orders.add(o.id);
        m.set(it.product_id, cur);
      }
    }
    return Array.from(m.values())
      .map((r) => ({ ...r, orders: r.orders.size, revenue: Math.round(r.revenue * 100) / 100 }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [filtered, applied]);

  const totals = useMemo(() => {
    const revenue = filtered.reduce((a, b) => a + b.total, 0);
    const items = filtered.reduce(
      (a, b) => a + b.items.reduce((x, it) => x + it.quantity, 0),
      0,
    );
    return { orders: filtered.length, revenue, items };
  }, [filtered]);

  const presetRange = (days) => {
    const t = new Date();
    const f = new Date();
    f.setDate(t.getDate() - days + 1);
    setFrom(f.toISOString().slice(0, 10));
    setTo(t.toISOString().slice(0, 10));
  };

  const clearFilters = () => {
    setFrom(firstOfMonth);
    setTo(today);
    setProductIds([]);
    setSalesmanId("all");
    setStoreType("all");
    setApplied({
      from: firstOfMonth,
      to: today,
      productIds: [],
      salesmanId: "all",
      storeType: "all",
    });
  };

  const toggleProduct = (pid) => {
    setProductIds((cur) =>
      cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid],
    );
  };

  const filteredProductOptions = products.filter((p) =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()),
  );

  const exportExcel = () => {
    // Helper: build cross-tab matrix
    const crossTab = (rowKey, colKey, valueFn, sortRows = true) => {
      const cols = new Set();
      const rows = new Map();
      for (const o of filtered) {
        for (const it of o.items) {
          if (applied.productIds.length > 0 && !applied.productIds.includes(it.product_id)) continue;
          const r = rowKey(o, it);
          const c = colKey(o, it);
          if (!r || !c) continue;
          cols.add(c);
          if (!rows.has(r)) rows.set(r, {});
          rows.get(r)[c] = (rows.get(r)[c] || 0) + valueFn(o, it);
        }
      }
      const colList = [...cols].sort();
      const rowList = [...rows.keys()];
      if (sortRows) rowList.sort();
      const headers = ["", ...colList, "Total"];
      const out = rowList.map((r) => {
        const obj = rows.get(r);
        const vals = colList.map((c) => Math.round((obj[c] || 0) * 100) / 100);
        const total = Math.round(vals.reduce((a, b) => a + b, 0) * 100) / 100;
        return [r, ...vals, total];
      });
      // Footer: column totals
      const footerVals = colList.map((c) => {
        const sum = rowList.reduce((a, r) => a + (rows.get(r)[c] || 0), 0);
        return Math.round(sum * 100) / 100;
      });
      const grand = Math.round(footerVals.reduce((a, b) => a + b, 0) * 100) / 100;
      out.push(["TOTAL", ...footerVals, grand]);
      return { headers, rows: out };
    };

    // Pivot 1: Revenue by Salesman × Date
    const pivotSalesmanDate = crossTab(
      (o) => o.salesman_name || "—",
      (o) => o.created_at.slice(0, 10),
      (o, it) => it.line_total,
    );
    // Pivot 2: Quantity by Product × Salesman
    const pivotProductSalesman = crossTab(
      (o, it) => it.product_name,
      (o) => o.salesman_name || "—",
      (o, it) => it.quantity,
    );
    // Pivot 3: Revenue by Store Type × Salesman
    const pivotTypeSalesman = crossTab(
      (o) => o.store_type,
      (o) => o.salesman_name || "—",
      (o, it) => it.line_total,
    );

    const filterSummary = [
      ["From", applied.from],
      ["To", applied.to],
      [
        "Products",
        applied.productIds.length === 0
          ? "All"
          : applied.productIds
              .map((pid) => products.find((p) => p.id === pid)?.name)
              .filter(Boolean)
              .join(" | "),
      ],
      [
        "Salesman",
        applied.salesmanId === "all"
          ? "All"
          : salesmen.find((s) => s.id === applied.salesmanId)?.name || "—",
      ],
      ["Store type", applied.storeType === "all" ? "All" : applied.storeType],
      ["Generated at", new Date().toLocaleString("en-IN")],
      [],
      ["Orders", totals.orders],
      ["Revenue (INR)", totals.revenue],
      ["Items sold", totals.items],
    ];

    const ordersRows = filtered.map((o) => {
      const s = storeMap.get(o.store_id) || {};
      return [
        new Date(o.created_at).toLocaleString("en-IN"),
        o.id,
        o.store_name,
        s.address || "",
        s.phone || "",
        o.store_type,
        o.salesman_name,
        o.items.length,
        o.items.reduce((a, it) => a + it.quantity, 0),
        o.total,
        o.notes || "",
      ];
    });

    const itemRows = [];
    for (const o of filtered) {
      const s = storeMap.get(o.store_id) || {};
      for (const it of o.items) {
        itemRows.push([
          new Date(o.created_at).toLocaleString("en-IN"),
          o.id,
          o.store_name,
          s.address || "",
          s.phone || "",
          o.store_type,
          o.salesman_name,
          it.product_name,
          it.quantity,
          it.unit_price,
          it.line_total,
        ]);
      }
    }

    const productRows = productBreakdown.map((p) => [
      p.product_name,
      p.qty,
      p.orders,
      p.revenue,
    ]);

    const pivotTips = [
      ["Tip", "How to use"],
      [
        "Pre-built pivots",
        "Sheets prefixed with 'Pivot:' are ready-made cross-tabs. Open any one and you'll see rows × columns with running totals.",
      ],
      [
        "Build your own",
        "1. Open the 'Order items' sheet.  2. Select all cells.  3. Insert → PivotTable → choose what to drag into Rows / Columns / Values.",
      ],
      [
        "Refresh",
        "Re-export from the app whenever data changes. Excel pivots can also be refreshed manually after re-pasting fresh data.",
      ],
      [
        "Recommended pivots",
        "Rows: Salesman / Product / Store; Columns: Date / Week; Values: Sum of line_total or quantity",
      ],
    ];

    downloadExcel(`GM_Kridam_Orders_${applied.from}_to_${applied.to}.xlsx`, [
      { name: "Summary", headers: ["Field", "Value"], rows: filterSummary },
      {
        name: "Pivot: Salesman × Date",
        headers: pivotSalesmanDate.headers,
        rows: pivotSalesmanDate.rows,
      },
      {
        name: "Pivot: Product × Salesman",
        headers: pivotProductSalesman.headers,
        rows: pivotProductSalesman.rows,
      },
      {
        name: "Pivot: Type × Salesman",
        headers: pivotTypeSalesman.headers,
        rows: pivotTypeSalesman.rows,
      },
      {
        name: "Product breakdown",
        headers: ["Product", "Qty sold", "Orders", "Revenue (INR)"],
        rows: productRows,
      },
      {
        name: "Orders",
        headers: [
          "When",
          "Order ID",
          "Store",
          "Address",
          "Phone",
          "Type",
          "Salesman",
          "# items",
          "Total qty",
          "Total (INR)",
          "Notes",
        ],
        rows: ordersRows,
      },
      {
        name: "Order items",
        headers: [
          "When",
          "Order ID",
          "Store",
          "Address",
          "Phone",
          "Type",
          "Salesman",
          "Product",
          "Qty",
          "Unit price",
          "Line total",
        ],
        rows: itemRows,
      },
      { name: "Pivot tips", headers: ["Tip", "How to use"], rows: pivotTips },
    ]);
  };

  const productLabel = useMemo(() => {
    if (productIds.length === 0) return "All products";
    if (productIds.length === 1) {
      const p = products.find((x) => x.id === productIds[0]);
      return p?.name || "1 product";
    }
    return `${productIds.length} products`;
  }, [productIds, products]);

  const breakdownTitle = useMemo(() => {
    if (applied.productIds.length === 0) return "All products in range";
    if (applied.productIds.length === 1) {
      const p = products.find((x) => x.id === applied.productIds[0]);
      return `Just: ${p?.name || ""}`;
    }
    return `Selected: ${applied.productIds.length} products`;
  }, [applied.productIds, products]);

  return (
    <div className="space-y-5" data-testid="admin-orders-page">
      <div>
        <p className="overline text-[color:var(--muted)]">History</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Orders</h1>
        <p className="text-[color:var(--muted)] mt-1">
          Filter by date, product, salesman or store type. Export anything you see.
        </p>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl border border-[color:var(--border)] bg-white p-4 ink-shadow flex flex-wrap items-end gap-3">
        <div>
          <label className="overline text-[color:var(--muted)]">From</label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 w-40"
            data-testid="orders-from-input"
          />
        </div>
        <div>
          <label className="overline text-[color:var(--muted)]">To</label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 w-40"
            data-testid="orders-to-input"
          />
        </div>
        <div className="flex gap-1.5">
          <Button variant="outline" onClick={() => presetRange(1)} className="h-10">Today</Button>
          <Button variant="outline" onClick={() => presetRange(7)} className="h-10">7d</Button>
          <Button variant="outline" onClick={() => presetRange(30)} className="h-10">30d</Button>
        </div>
        <div className="min-w-[220px]">
          <label className="overline text-[color:var(--muted)]">Products</label>
          <Popover open={productPopOpen} onOpenChange={setProductPopOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-10 w-full justify-between font-normal"
                data-testid="orders-product-filter-trigger"
              >
                <span className="truncate text-left">
                  {productLabel}
                  {productIds.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center text-[10px] rounded-full bg-[color:var(--primary)] text-white px-1.5 py-0.5 align-middle">
                      {productIds.length}
                    </span>
                  )}
                </span>
                <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <div className="p-2 border-b border-[color:var(--border)]">
                <Input
                  placeholder="Search products…"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="h-9"
                  data-testid="orders-product-filter-search"
                />
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {filteredProductOptions.length === 0 && (
                  <div className="px-3 py-4 text-sm text-[color:var(--muted)] text-center">
                    No matches
                  </div>
                )}
                {filteredProductOptions.map((p) => {
                  const checked = productIds.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[color:var(--surface-2)]"
                      data-testid={`orders-product-opt-${p.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleProduct(p.id)}
                      />
                      <span className="text-sm font-tamil flex-1 truncate">
                        {p.name}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="border-t border-[color:var(--border)] p-2 flex items-center justify-between">
                <span className="text-xs text-[color:var(--muted)]">
                  {productIds.length} selected
                </span>
                <div className="flex gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => setProductIds([])}
                    disabled={productIds.length === 0}
                    data-testid="orders-product-clear-all"
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                    onClick={() => setProductPopOpen(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="min-w-[160px]">
          <label className="overline text-[color:var(--muted)]">Salesman</label>
          <Select value={salesmanId} onValueChange={setSalesmanId}>
            <SelectTrigger className="h-10" data-testid="orders-salesman-filter">
              <SelectValue placeholder="All salesmen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salesmen</SelectItem>
              {salesmen.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="overline text-[color:var(--muted)]">Type</label>
          <Select value={storeType} onValueChange={setStoreType}>
            <SelectTrigger className="h-10 w-32" data-testid="orders-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="wholesale">Wholesale</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={clearFilters} className="h-10" data-testid="orders-clear-filter">
            <FilterX className="h-4 w-4 mr-1" /> Clear
          </Button>
          <Button
            onClick={submitFilters}
            disabled={!isDirty}
            className={`h-10 text-white ${
              isDirty
                ? "bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)]"
                : "bg-[color:var(--muted)] cursor-not-allowed"
            }`}
            data-testid="orders-submit-filter"
          >
            {isDirty ? "View report" : "Up to date"}
          </Button>
          <Button
            onClick={exportExcel}
            disabled={filtered.length === 0}
            variant="outline"
            className="h-10"
            data-testid="orders-export-button"
          >
            <Download className="h-4 w-4 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Kpi icon={ShoppingBag} label="Orders" value={totals.orders} tone="moss" />
        <Kpi icon={IndianRupee} label="Revenue" value={fmtINR(totals.revenue)} tone="primary" />
        <Kpi icon={Package} label="Items sold" value={totals.items} tone="mustard" />
      </div>

      {/* Product breakdown for the filtered range */}
      <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-[color:var(--surface-2)]">
          <div>
            <p className="overline">Product breakdown</p>
            <h3 className="font-display font-bold">
              {breakdownTitle}
            </h3>
          </div>
          <span className="text-xs text-[color:var(--muted)]">
            {productBreakdown.length} item{productBreakdown.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="product-breakdown-table">
            <thead>
              <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3 text-right">Qty sold</th>
                <th className="px-4 py-3 text-right">Orders</th>
                <th className="px-4 py-3 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {productBreakdown.map((p) => (
                <tr key={p.product_id} className="border-t border-[color:var(--border)]">
                  <td className="px-4 py-3 font-medium font-tamil">{p.product_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.qty}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.orders}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">
                    {fmtINR(p.revenue)}
                  </td>
                </tr>
              ))}
              {productBreakdown.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-6 text-center text-[color:var(--muted)]">
                    Nothing sold in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Orders table */}
      <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-[color:var(--surface-2)]">
              <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Store</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Salesman</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-[color:var(--border)] hover:bg-[color:var(--surface-2)]/40 cursor-pointer"
                  onClick={() => setSelected(o)}
                  data-testid={`order-row-${o.id}`}
                >
                  <td className="px-4 py-3 text-[color:var(--muted)] whitespace-nowrap">
                    {new Date(o.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    <div>{o.store_name}</div>
                    {(() => {
                      const s = storeMap.get(o.store_id);
                      if (!s) return null;
                      return (
                        <div className="text-xs text-[color:var(--muted)] font-normal space-y-0.5 mt-0.5">
                          {s.address && (
                            <div className="truncate max-w-[260px]" title={s.address}>
                              {s.address}
                            </div>
                          )}
                          {s.phone && (
                            <a
                              href={`tel:${s.phone}`}
                              className="text-[color:var(--primary)] hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {s.phone}
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        o.store_type === "wholesale"
                          ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                          : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                      }`}
                    >
                      {o.store_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">{o.salesman_name}</td>
                  <td className="px-4 py-3">{o.items.length}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    <div className={o.status === "cancelled" ? "line-through text-[color:var(--muted)]" : ""}>
                      {fmtINR(o.total)}
                    </div>
                    {o.status === "cancelled" && (
                      <div
                        className="text-[9px] mt-0.5 px-1.5 py-0.5 rounded-full bg-[color:var(--primary)]/10 text-[color:var(--primary)] uppercase tracking-wide font-semibold inline-block"
                        data-testid={`order-cancelled-${o.id}`}
                      >
                        Cancelled
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-[color:var(--muted)]">
                    No orders match your filters.
                  </td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-[color:var(--surface-2)] border-t border-[color:var(--border)]">
                  <td className="px-4 py-3 font-display font-bold uppercase tracking-wide text-xs" colSpan="4">
                    Total · {filtered.length} orders
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {totals.items} items
                  </td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums">
                    {fmtINR(totals.revenue)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle>Order details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <p className="overline">Store</p>
                  <p className="font-medium">{selected.store_name}</p>
                  {(() => {
                    const s = storeMap.get(selected.store_id);
                    if (!s) return null;
                    return (
                      <div className="text-xs text-[color:var(--muted)] font-normal mt-1 space-y-0.5">
                        {s.address && <div>{s.address}</div>}
                        {s.owner_name && <div>Owner: {s.owner_name}</div>}
                        {s.phone && (
                          <a
                            href={`tel:${s.phone}`}
                            className="text-[color:var(--primary)] hover:underline inline-block"
                          >
                            📞 {s.phone}
                          </a>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <p className="overline">Type</p>
                  <p className="font-medium capitalize">{selected.store_type}</p>
                </div>
                <div>
                  <p className="overline">Salesman</p>
                  <p className="font-medium">{selected.salesman_name}</p>
                </div>
                <div className="col-span-2">
                  <p className="overline">When</p>
                  <p className="font-medium">
                    {new Date(selected.created_at).toLocaleString("en-IN")}
                  </p>
                </div>
              </div>
              <div className="border-t border-[color:var(--border)] pt-3">
                <p className="overline mb-2">Items</p>
                <ul className="divide-y divide-[color:var(--border)]">
                  {selected.items.map((it) => (
                    <li
                      key={it.product_id}
                      className="py-2 flex items-center justify-between text-sm"
                    >
                      <div className="font-tamil">
                        {it.product_name}{" "}
                        <span className="text-[color:var(--muted)]">× {it.quantity}</span>
                      </div>
                      <div className="tabular-nums">{fmtINR(it.line_total)}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-between border-t border-[color:var(--border)] pt-3">
                <span className="font-display text-lg">Total</span>
                <span className="font-display text-xl font-bold">{fmtINR(selected.total)}</span>
              </div>
              {selected.notes && (
                <div className="border-t border-[color:var(--border)] pt-3 text-sm">
                  <p className="overline mb-1">Notes</p>
                  <p>{selected.notes}</p>
                </div>
              )}
              {selected.status === "cancelled" && (
                <div className="border-t border-[color:var(--border)] pt-3 text-sm rounded-lg bg-[color:var(--primary)]/5 p-3">
                  <p className="overline text-[color:var(--primary)] mb-1">Cancelled</p>
                  {selected.cancellation_reason && (
                    <p className="text-[color:var(--ink)]">{selected.cancellation_reason}</p>
                  )}
                  <p className="text-xs text-[color:var(--muted)] mt-1">
                    {selected.cancelled_at &&
                      new Date(selected.cancelled_at).toLocaleString("en-IN")}
                  </p>
                </div>
              )}
              <div className="border-t border-[color:var(--border)] pt-3 flex flex-wrap gap-2 justify-end">
                {selected.status !== "cancelled" ? (
                  <>
                    <WhatsAppShareButton order={selected} store={storeMap.get(selected.store_id)} />
                    <Button
                      variant="outline"
                      onClick={openEditItems}
                      data-testid="order-edit-items-button"
                    >
                      <Pencil className="h-4 w-4 mr-1" /> Alter items
                    </Button>
                    <Button
                      onClick={openCancel}
                      className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                      data-testid="order-cancel-button"
                    >
                      <Ban className="h-4 w-4 mr-1" /> Cancel order
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={restoreOrder}
                    variant="outline"
                    data-testid="order-restore-button"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" /> Restore
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel order dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle>Cancel order</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="rounded-lg bg-[color:var(--primary)]/5 border border-[color:var(--primary)]/20 p-3 text-sm">
                <div>
                  <b>{selected.store_name}</b>
                </div>
                <div className="text-[color:var(--muted)]">
                  Total {fmtINR(selected.total)} · {selected.items.length} items
                </div>
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. shop returned all items, billing error…"
                  data-testid="cancel-reason-input"
                />
              </div>
              <div>
                <Label>Refund amount (optional)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={cancelRefund}
                  onChange={(e) => setCancelRefund(e.target.value)}
                  placeholder="Leave empty if nothing was paid"
                  className="h-12 font-mono"
                  data-testid="cancel-refund-input"
                />
                <div className="text-xs text-[color:var(--muted)] mt-1">
                  Posts a verified refund (negative payment) against the store ledger.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelOpen(false)}>
                  Keep order
                </Button>
                <Button
                  onClick={submitCancel}
                  disabled={cancelSaving}
                  className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                  data-testid="cancel-confirm-button"
                >
                  {cancelSaving ? "Cancelling…" : "Cancel order"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Alter items dialog (partial cancellation = reduce qty / remove items) */}
      <Dialog open={editItemsOpen} onOpenChange={setEditItemsOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-items-dialog">
          <DialogHeader>
            <DialogTitle>Alter order items</DialogTitle>
          </DialogHeader>
          {selected && (() => {
            const productMap = new Map(products.map((p) => [p.id, p]));
            const priceFor = (p) =>
              editStoreType === "wholesale" ? p.wholesale_price : p.retail_price;
            const editingIds = Object.keys(editItems);
            const tierChanged = editStoreType !== selected.store_type;
            const liveTotal = editingIds.reduce((sum, pid) => {
              const qty = Number(editItems[pid] || 0);
              if (!qty) return sum;
              const orig = selected.items.find((x) => x.product_id === pid);
              const p = productMap.get(pid);
              // If admin switched the pricing tier, recompute against catalog price.
              // If unchanged, prefer the original locked-in unit_price.
              const unit = tierChanged
                ? p
                  ? priceFor(p)
                  : orig?.unit_price ?? 0
                : orig?.unit_price ?? (p ? priceFor(p) : 0);
              return sum + qty * unit;
            }, 0);
            const addableProducts = products
              .filter((p) => p.active !== false)
              .filter((p) => !editingIds.includes(p.id))
              .filter((p) =>
                editProductSearch
                  ? p.name.toLowerCase().includes(editProductSearch.toLowerCase())
                  : true,
              );
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-[color:var(--muted)] flex-1">
                    Adjust quantities, switch pricing tier, or add new products.
                  </div>
                  <div
                    className="inline-flex items-center rounded-full border border-[color:var(--border)] p-0.5 bg-white shrink-0"
                    data-testid="admin-edit-pricing-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => setEditStoreType("retail")}
                      className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
                        editStoreType === "retail"
                          ? "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
                          : "text-[color:var(--muted)]"
                      }`}
                      data-testid="admin-edit-pricing-retail"
                    >
                      Retail
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditStoreType("wholesale")}
                      className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
                        editStoreType === "wholesale"
                          ? "bg-[color:var(--mustard)]/25 text-[#9A742E]"
                          : "text-[color:var(--muted)]"
                      }`}
                      data-testid="admin-edit-pricing-wholesale"
                    >
                      Wholesale
                    </button>
                  </div>
                </div>
                {tierChanged && (
                  <div
                    className="text-xs px-3 py-2 rounded-lg bg-[color:var(--mustard)]/15 text-[#7A5A1F] border border-[color:var(--mustard)]/30"
                    data-testid="admin-edit-tier-changed-banner"
                  >
                    Pricing tier switched from <b>{selected.store_type}</b> to <b>{editStoreType}</b>. All existing line items will be repriced on save.
                  </div>
                )}
                <ul className="divide-y divide-[color:var(--border)] border border-[color:var(--border)] rounded-lg max-h-72 overflow-y-auto">
                  {editingIds.length === 0 && (
                    <li className="p-3 text-xs text-[color:var(--muted)]">No items. Add a product below.</li>
                  )}
                  {editingIds.map((pid) => {
                    const orig = selected.items.find((x) => x.product_id === pid);
                    const p = productMap.get(pid);
                    const name = orig?.product_name || p?.name || pid;
                    const unit = tierChanged
                      ? p
                        ? priceFor(p)
                        : orig?.unit_price ?? 0
                      : orig?.unit_price ?? (p ? priceFor(p) : 0);
                    const qty = editItems[pid] ?? 0;
                    const isNew = !orig;
                    const priceChanged = orig && unit !== orig.unit_price;
                    return (
                      <li
                        key={pid}
                        className="flex items-center gap-2 p-3"
                        data-testid={`edit-row-${pid}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {name}
                            {isNew && (
                              <span className="ml-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-[color:var(--primary)]/10 text-[color:var(--primary)]">
                                new
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[color:var(--muted)] tabular-nums">
                            {fmtINR(unit)}
                            {priceChanged && (
                              <span className="ml-1 text-[color:var(--mustard)]">
                                (was {fmtINR(orig.unit_price)})
                              </span>
                            )}
                            {orig && <> × original {orig.quantity}</>}
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() =>
                            setEditItems((m) => ({ ...m, [pid]: Math.max(0, (m[pid] ?? 0) - 1) }))
                          }
                          className="h-9 w-9"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          value={qty}
                          onChange={(e) =>
                            setEditItems((m) => ({ ...m, [pid]: Math.max(0, Number(e.target.value) || 0) }))
                          }
                          className="w-16 h-9 text-center font-mono"
                          data-testid={`edit-qty-${pid}`}
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() =>
                            setEditItems((m) => ({ ...m, [pid]: (m[pid] ?? 0) + 1 }))
                          }
                          className="h-9 w-9"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Add product to this bill</Label>
                    <span className="text-[11px] text-[color:var(--muted)] tabular-nums">
                      {addableProducts.length} of {products.filter((p) => p.active !== false).length} available
                    </span>
                  </div>
                  <Input
                    placeholder="Search products by name…"
                    value={editProductSearch}
                    onChange={(e) => setEditProductSearch(e.target.value)}
                    className="h-9"
                    data-testid="edit-add-product-search"
                  />
                  {addableProducts.length > 0 && (
                    <ul className="border border-[color:var(--border)] rounded-lg divide-y divide-[color:var(--border)] max-h-72 overflow-y-auto">
                      {addableProducts.map((p) => (
                        <li
                          key={p.id}
                          className="flex items-center justify-between gap-2 p-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{p.name}</div>
                            <div className="text-[11px] text-[color:var(--muted)] tabular-nums">
                              {fmtINR(priceFor(p))}{" "}
                              <span className="text-[10px]">
                                · retail {fmtINR(p.retail_price)} · w/s {fmtINR(p.wholesale_price)}
                              </span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditItems((m) => ({ ...m, [p.id]: (m[p.id] ?? 0) + 1 }));
                            }}
                            data-testid={`edit-add-product-${p.id}`}
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {addableProducts.length === 0 && (
                    <div className="text-xs text-[color:var(--muted)] px-1">
                      {editProductSearch
                        ? "No matching products."
                        : "All products are already in this order."}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between border-t border-[color:var(--border)] pt-2">
                  <span className="text-xs uppercase tracking-wide text-[color:var(--muted)]">
                    Live total
                  </span>
                  <span className="font-display font-bold text-lg tabular-nums">
                    {fmtINR(liveTotal)}
                  </span>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditItemsOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={submitEditItems}
                    disabled={editSaving}
                    className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                    data-testid="edit-items-save"
                  >
                    {editSaving ? "Saving…" : "Save"}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "bg-[color:var(--primary)]/10 text-[color:var(--primary)]",
    moss: "bg-[color:var(--moss)]/10 text-[color:var(--moss)]",
    mustard: "bg-[color:var(--mustard)]/20 text-[#9A742E]",
  };
  return (
    <div className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow">
      <div className="flex items-center justify-between">
        <span className="overline text-[color:var(--muted)]">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-[color:var(--ink)]">{value}</div>
    </div>
  );
}
